import React, { useEffect, useRef, useState } from 'react';
import 'styles/home.css';
import 'styles/quick-look.css';

import API from 'files/api.js';
import { CORE_QUESTIONS, DEPTH_QUESTIONS, answerFor, buildModelPayload, firstUnanswered, resolveQuestion, scoreAnswers } from 'files/quick-look-model.js';
import { clear, isComplete, isInProgress, load, save } from 'files/quick-look-storage.js';
import { requestRead } from 'files/quick-look-read.js';
import { campaign, trackEvent } from 'files/analytics.js';
import BotTrap, { trapValue } from 'views/BotTrap.js';

/* The Quick Look. Flow follows quick-look.md:
   intro → core (7) → depth opt-in → depth (4) → one optional free-text box →
   results on screen, free and ungated → optional form to be put in touch with
   someone.

   Rules that matter if you edit this:
   - Never gate the read behind the form. The form is offered after the value
     has already been handed over, not before, and what it offers is a person to
     go through it with — never the useful part held back.
   - One question per screen, single click to answer, no typing until the very
     last optional box.
   - The read is pattern level and says so. What is actually worth doing about
     it is a conversation, not a longer document.
   - No scores, grades or gauges anywhere in the read. See quick-look-model.js.
   - Progress is written to storage on every answer. An owner interrupted at
     question four should never be asked to start again.
   - Every button that opens the tool reads "Take a Quick Look", sitewide. It
     used to read "Let's take a look", on the rule that the name was a
     destination rather than an instruction. Mike changed that: the instruction
     form says what pressing it does, which matters more on a page a stranger
     is scanning. In prose it is still "your Quick Look". */

const STAGES = {
    intro: 'intro',
    core: 'core',
    gate: 'gate',
    depth: 'depth',
    notes: 'notes',
    results: 'results'
};

/* What the screen counts through while the read is being put together.

   The first four name the parts of the read, so the wait doubles as a preview of
   the shape of what is coming rather than a spinner with nothing behind it.

   The fifth is there because the sequence used to end on finding one thing the
   owner could do, which is the last piece of thinking and reads as the finish.
   The panel then sat on a completed list for the remaining seconds, which is a
   worse silence than no list at all: every line says done, and nothing is
   happening. Writing it up is real work, it is genuinely the last of it, and it
   is the step to be sitting on when the read lands.

   They advance on a timer, not on anything the server reports: one request goes
   out and one comes back, and there is no honest sub-progress to read. What
   keeps that fair is that each line describes work that is genuinely happening,
   and the read renders the moment it arrives rather than waiting for the
   sequence to finish. Nobody is ever held back to watch this. */
const READING_STEPS = [
    'Reading your answers',
    'Looking at where they point',
    'Working out what that means for a business like yours',
    'Finding one thing you can do yourself',
    'Writing your read'
];

/* The exact wording shown beside the SMS checkbox. It is sent with the
   submission rather than assumed server-side, because consent is a dated record
   of what someone actually agreed to — the wording they saw is part of the
   record, not a detail. See public/articles/texting-consent.md. */
const SMS_CONSENT_TEXT = 'It’s okay to text me about my results and next steps. Message and data rates may apply. Reply STOP anytime to opt out.';

const QuickLook = ({ intent, onClose, shared, utils }) => {

    const stageRef = useRef(null);

    // true once this visit has answered or changed anything, which is what makes
    // closing worth confirming — see onRequestClose
    const touched = useRef(false);
    const [confirming, setConfirming] = useState(false);

    // the modal animates in on mount, so it has to animate out before it goes —
    // unmounting straight away made it vanish rather than close
    const [leaving, setLeaving] = useState(false);

    const saved = useRef(load());

    const [answers, setAnswers] = useState(saved.current ? saved.current.answers || {} : {});
    const [freeText, setFreeText] = useState(saved.current ? saved.current.freeText || '' : '');
    const [index, setIndex] = useState(0);

    // the contact form. `sms` starts false and is only ever changed by the owner
    // clicking the box — nothing else in here may set it true.
    const [lead, setLead] = useState({ email: '', first: '', last: '', phone: '', sms: false });
    const [leadState, setLeadState] = useState(null); // null | 'sending' | 'sent' | error string

    /* "Just send me the read", which is a different intent from asking to be put
       in touch and is kept apart from it everywhere — separate state here, a
       separate action on the server, a separate table behind that. Folding the
       two together would inflate the number that gets uploaded to Google as a
       conversion, and it would then bid toward the cheaper, weaker action. */
    const [copyTo, setCopyTo] = useState('');
    const [copyState, setCopyState] = useState(null); // null | 'sending' | 'sent' | error string

    /* The generated read, and whether we are still waiting on it. It is kept
       with the answers so that reopening a finished Quick Look shows the same
       read rather than a second opinion on the same business — and so we do not
       pay to write it twice. Null read plus a settled state means the authored
       read is what renders; see quick-look-read.js. */
    const [read, setRead] = useState(saved.current ? saved.current.read || null : null);
    const [readingStep, setReadingStep] = useState(0);

    /* Whether this visit opened onto a Quick Look that was already finished.

       It is the difference between an owner who has just answered and one who
       has come back to what they answered before, and the model is only ever
       asked on behalf of the first. A reload, a second visit, a tab restored a
       week later: none of those are new information about a business, and
       paying to have the same answers read again would be paying for the same
       sentences twice.

       It has to be a ref rather than derived from the record, because both of
       the things that would tell us are gone by then: the read gets cached under
       the same record, and re-answering does not change that this session began
       on a finished one. */
    const returning = useRef(isComplete(saved.current));

    /* Only "reading" if a read is actually going to be asked for. Left true for
       a returning owner with nothing cached, the results screen would sit on the
       waiting state with nothing on its way to end it. */
    const [reading, setReading] = useState(shared ? true : (!returning.current && !(saved.current && saved.current.read)));

    /* A read that arrived by link rather than by answering.

       Somebody opening this did not fill anything in — they were sent it, and in
       a business of this size that is usually the point: the partner, the person
       who runs operations, whoever else has a say. So none of the local record
       applies to them and none of it gets written. They see the read and the same
       offer to talk that the owner saw. */
    const sharedRead = useRef(shared || null);

    /* Every write to the local record goes through here.

       A visitor who arrived on a forwarded link answered nothing, and anything
       stored under their name is a Quick Look they never took: open the tool
       later and it offers to resume from somebody else's session, pre-filled
       with somebody else's notes.

       Guarding the auto-save effect was not enough, because the lead form writes
       twice on its own — the free text on submit and the email on success — and
       a recipient asking to talk is a perfectly ordinary thing to happen. One
       gate, so the next write added to this file cannot miss it. */
    const remember = fields => {
        if(sharedRead.current) return;
        save(fields);
    };

    // a finished Quick Look reopens on its results; an interrupted one offers to
    // resume from the intro rather than dropping the owner mid-question
    const [stage, setStage] = useState(shared || isComplete(saved.current) ? STAGES.results : STAGES.intro);

    useEffect(() => {
        document.body.classList.add('dl-body');
        return () => document.body.classList.remove('dl-body');
    }, []);

    /* Fetching a shared read.

       The uuid names a row and the server hands back what it wrote against it,
       which is the same rule the lead submission follows: nothing about what an
       owner was shown is ever accepted from a request body.

       A dead link settles rather than spinning. The results screen falls through
       to the authored read when there is no generated one, so somebody who opens
       a link that has expired still lands on something worth reading instead of
       a loading state that never ends. */
    useEffect(() => {

        if(!sharedRead.current) return;
        let live = true;

        (async () => {
            try {
                const response = await fetch(`/api/v${API.version}/quick_look/`, {
                    body: JSON.stringify({ read_id: sharedRead.current, type: 'recall' }),
                    headers: { 'Content-Type': 'application/json' },
                    method: 'POST'
                });
                const body = response.ok ? await response.json() : null;
                if(live && body && body.read) {
                    setRead({ ...body.read, id: body.id });
                }
            } catch(e) {
                // falls through to the authored read, which is a real page
            }
            if(live) setReading(false);
        })();

        return () => { live = false; };
    }, []);

    // Moving between questions replaces the screen, so focus and scroll have to
    // be reset by hand or a keyboard user is left at the bottom of the panel.
    // The modal scrolls itself, not the window.
    useEffect(() => {
        if(stageRef.current) {
            stageRef.current.scrollTop = 0;
            stageRef.current.focus({ preventScroll: true });
        }
    }, [stage, index]);

    /* Opened by somebody asking to be put in touch, rather than by somebody
       coming to do the Quick Look.

       They have read their read already, on the article page they clicked from,
       and the thing they said yes to is at the very bottom of a screen two or
       three turns long. Landing them at the top means the answer to "talk it
       through with someone" is a scroll, which is a small tax charged at the
       exact moment somebody has decided to get in touch.

       Scrolled rather than jumped, and only once. The read travelling past on
       the way down is doing real work: it says the form belongs to that read
       rather than being a generic contact box, and it leaves them knowing there
       is something above them to go back to. */
    const asked = useRef(intent === 'contact');

    useEffect(() => {

        const panel = stageRef.current;
        const form = panel && panel.querySelector('.ql-ask');

        if(!asked.current || stage !== STAGES.results || !form) {
            return;
        }

        // spent on the first pass that finds the form, so a later render cannot
        // drag somebody back down after they have started reading
        asked.current = false;

        const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        /* Held until the modal has finished arriving. The panel is mid-transform
           for its first 380ms, so a scroll started inside that window is
           measured against a box that is still moving. */
        const timer = window.setTimeout(() => {

            const stage = stageRef.current;
            const target = stage && stage.querySelector('.ql-ask');
            if(!target) {
                return;
            }

            /* Flush to the heading, not a little above it. The pull quote sits
               directly against it with no gap, so any clearance lands mid-line
               and leaves a sliver of a sentence at the top edge that reads as a
               rendering fault rather than as context. */
            const top = stage.scrollTop
                + target.getBoundingClientRect().top
                - stage.getBoundingClientRect().top;
            stage.scrollTo({ behavior: still ? 'auto' : 'smooth', top: Math.max(0, top) });

        }, still ? 0 : 420);

        return () => window.clearTimeout(timer);

    }, [read, reading, stage]);

    /* Written the moment they reach the read, from everything they answered.
       Nothing about this can fail loudly: if the server is slow, down, or has
       not been built yet, `requestRead` resolves null and the authored read
       renders in its place. */
    useEffect(() => {

        /* Three ways to have nothing to ask: not on the read yet, holding one
           already, or here to look at one written on an earlier visit. The last
           is the one that matters — a returning owner with no cached read gets
           the site's authored one rather than a fresh call, every time, until
           they change an answer and ask for a new read themselves. */
        if(stage !== STAGES.results || read || returning.current) return;

        /* Two ways to be on this screen without having answered anything.

           A shared read is the obvious one: the visitor arrived on a link, the
           read is being fetched by uuid, and asking the model to write a second
           one about a business it knows nothing about is both wrong and paid for.
           It fired on every shared load and the server rejected it, which is the
           400 that appeared in the console while the page looked fine — the
           authored read renders on failure, so the screen hid it completely.

           The empty check is the belt to that brace. A read request carrying no
           answers cannot produce anything worth showing however it got here, so
           it does not leave the browser. */
        if(sharedRead.current) return;
        if(Object.keys(answers || {}).length === 0) {
            // nothing is coming, so nothing may be left waiting on it
            setReading(false);
            return;
        }

        let cancelled = false;
        setReading(true);

        requestRead(buildModelPayload(answers, freeText)).then(result => {
            if(cancelled) return;
            setReading(false);
            if(result) {
                setRead(result);
                remember({ read: result });
            }
        });

        return () => { cancelled = true; };

    }, [answers, freeText, read, stage]);

    /* Walks the steps while the read is being written, and stops on the last
       one rather than looping — a sequence that starts over says the work
       started over, which is the one thing it must not say. */
    useEffect(() => {

        if(stage !== STAGES.results || read || !reading) return;

        setReadingStep(0);
        const timer = window.setInterval(() => {
            setReadingStep(step => Math.min(step + 1, READING_STEPS.length - 1));
        /* Paced across the wait rather than across a guess at it. At nineteen
           hundred the steps were spent by the sixth second and the panel then sat
           perfectly still for the rest of a fifteen second read, which is the
           exact shape of a page that has crashed.

           Set so the last step goes live at about thirteen seconds, which is
           where a read of this length usually lands. The one before it stays up
           through the window where most arrive, and anybody unlucky enough to
           wait longer waits on "writing your read" rather than on a list with
           every line already ticked. */
        }, 3400);

        return () => window.clearInterval(timer);

    }, [read, reading, stage]);

    const questions = stage === STAGES.depth ? DEPTH_QUESTIONS : CORE_QUESTIONS;

    /* Asked the way this owner should be asked it. A startup gets different
       wording and different options on four of the seven core questions — see
       the segments note in quick-look-model.js. Everything downstream stores and
       scores the position they picked, so the question has to be resolved here,
       once, rather than anywhere it is read back. */
    const question = resolveQuestion(questions[index], answers);

    const onAnswer = (id, value) => {

        touched.current = true;
        const next = { ...answers, [id]: value };
        setAnswers(next);

        const finishing = index >= questions.length - 1;
        const nextStage = finishing ? (stage === STAGES.core ? STAGES.gate : STAGES.notes) : stage;

        // written on every answer, so closing the tab never costs progress
        remember({ answers: next });

        if(!finishing) {
            setIndex(index + 1);
            return;
        }
        setIndex(0);
        setStage(nextStage);
    }

    /* Put them down at the first core question we do not have a usable answer
       for. Nothing about where they were is stored: a record with every core
       question answered is finished by definition and opens on the read, so the
       only thing left to resume into is the core set, and the gap in it is
       wherever it is today rather than wherever it was when they left. */
    const onResume = () => {
        const next = firstUnanswered(CORE_QUESTIONS, answers);
        trackEvent('quick-look-started', { entry: 'resumed' });
        setIndex(next === -1 ? 0 : next);
        setStage(STAGES.core);
    }

    /* Everything past the last question is optional, so leaving the free-text
       box empty has to be as easy as filling it in. Both routes land on the
       same finished record. */
    const onFinish = () => {
        touched.current = true;
        remember({ freeText: freeText });
        trackEvent('quick-look-completed', { depth: 'full', notes: freeText.trim() ? 'yes' : 'no' });
        setStage(STAGES.results);
    }

    /* Reviewing and changing answers is the whole point of keeping them. The
       read is dropped on the way back: a read written from the old answers
       would be quoting things they have just changed. */
    const onReview = () => {
        returning.current = false;
        setRead(null);
        setReading(true);
        remember({ read: null });
        setIndex(0);
        setStage(STAGES.core);
    }

    const onBack = () => {
        if(stage === STAGES.notes) {
            setIndex(DEPTH_QUESTIONS.length - 1);
            setStage(STAGES.depth);
            return;
        }
        if(index > 0) {
            setIndex(index - 1);
            return;
        }
        if(stage === STAGES.depth) {
            setStage(STAGES.gate);
            return;
        }
        setStage(STAGES.intro);
    }

    const onRestart = () => {

        /* Somebody who read a forwarded link and wants their own.

           Two things have to stop being true for that to work. The record gate
           has to open, or they answer twelve questions and none of it is kept.
           And the address has to stop naming a read that is not theirs, so a
           reload does not drop them back into somebody else's results — history
           is App's to push, but this is a correction to an address the app has
           no reason to revisit, so it is replaced here. */
        if(sharedRead.current) {
            sharedRead.current = null;
            try {
                window.history.replaceState({}, '', '/quick-look');
            } catch(e) {
                // an address that stays wrong is survivable; a crash is not
            }
        }

        clear();
        saved.current = null;
        setAnswers({});
        setLead({ email: '', first: '', last: '', phone: '', sms: false });
        setLeadState(null);
        setFreeText('');
        setRead(null);
        setReading(true);
        returning.current = false;
        setIndex(0);
        setStage(STAGES.intro);
    }

    /* Closing mid-flow looks like losing the answers already given, so say
       plainly that they are kept and where. A finished Quick Look, or one that
       has not been touched this visit, closes without the interruption — there
       is nothing at stake to warn about. */
    const onRequestClose = evt => {
        if(evt) {
            evt.preventDefault();
        }
        if(sharedRead.current) return;
        if(touched.current && stage !== STAGES.results) {
            setConfirming(true);
            return;
        }
        // through onExit, never straight to onClose, or the modal vanishes
        // instead of closing on every path except the confirmation dialog
        onExit();
    }

    /* Plays the exit, then hands back to the app. The delay matches the animation
       in quick-look.css — if you change one, change the other. Anyone who has asked
       their system not to animate closes immediately. */
    const onExit = evt => {

        if(evt) {
            evt.preventDefault();
        }
        if(leaving) {
            return;
        }
        if(window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            onClose();
            return;
        }
        setLeaving(true);
        window.setTimeout(onClose, 260);
    }

    const onContact = evt => {
        evt.preventDefault();
        utils.navigation.set({ view: 'contact' });
    }

    /* Straight to the piece, without the exit guard the close button runs. That
       guard exists to stop a part-finished Quick Look vanishing unannounced, and
       this link only appears at the foot of a finished one. Their answers are
       saved either way, so the read is here when they come back. */
    const onArticle = slug => evt => {
        evt.preventDefault();
        utils.navigation.set({ slug: slug, view: 'article' });
    }

    // Escape runs through the same guard as the close button, so it can never
    // discard a part-finished Quick Look without saying what happens to it.
    useEffect(() => {
        const onKeyDown = evt => {
            if(evt.key !== 'Escape') {
                return;
            }
            if(confirming) {
                setConfirming(false);
                return;
            }
            onRequestClose();
        }
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    });

    /* Hands the owner and their answers to a person. The read they saw goes with
       it, so whoever picks it up is looking at exactly what the owner is looking
       at rather than reconstructing it.

       If this call fails the owner still has the whole read, so it only ever
       reports the truth about the send itself — it never claims a submission
       that did not happen.

       SMS consent travels as a dated event carrying the wording the owner saw,
       never as a bare flag, and it is only present when they ticked the box.
       Submitting the form is not consent to be texted, and nothing here may
       infer it from a phone number being filled in. */
    const onSubmitLead = async evt => {

        evt.preventDefault();
        if(leadState === 'sending') return;
        const trap = trapValue(evt);

        setLeadState('sending');
        remember({ freeText: freeText });
        try {
            let response = await fetch(`/api/v${API.version}/quick_look/`, {
                body: JSON.stringify({
                    company_website: trap,
                    email: lead.email.trim(),
                    first_name: lead.first.trim(),
                    last_name: lead.last.trim(),
                    phone: lead.phone.trim() || null,
                    payload: buildModelPayload(answers, freeText),

                    /* The uuid, not the read. The server wrote it and still has
                       it; naming the row is enough, and it means what gets
                       attached to this lead cannot be edited on the way back. */
                    read_id: read ? read.id : null,
                    sms_consent: lead.sms ? {
                        granted: true,
                        granted_at: new Date().toISOString(),
                        source: 'quick-look-results',
                        text: SMS_CONSENT_TEXT
                    } : { granted: false },
                    type: 'readout',

                    /* How this visit arrived, stored with the lead rather than
                       only in analytics. Umami can tell us how many leads a
                       campaign produced; only this can tell us which lead, which
                       is what an offline conversion upload needs to credit the
                       click that actually became a conversation. */
                    campaign: campaign()
                }),
                headers: { 'Content-Type': 'application/json' },
                method: 'POST'
            });
            if(response.status !== 200) {
                throw new Error('That did not go through.');
            }
            setLeadState('sent');
            trackEvent('lead-submitted', { from: 'quick-look', sms: lead.sms ? 'yes' : 'no' });
            remember({ email: lead.email.trim() });

        } catch(e) {
            setLeadState('That did not go through. Email hello@duralabs.io and we will pick it up from there.');
        }
    }

    const getBar = () => {
        return (
            <div className={'ql-bar'}>
                <span className={'dl-mark'}>
                    <img alt={''} src={'/images/dr-logo-circle.png'} style={{ height: 25, width: 25 }} />
                    Quick Look
                </span>
                <button
                aria-label={'Close the Quick Look'}
                className={'ql-exit'}
                onClick={onRequestClose}
                type={'button'}>
                    Close <span aria-hidden={'true'}>✕</span>
                </button>
            </div>
        )
    }

    const getConfirm = () => {
        return (
            <div className={'ql-confirm'}>
                <div className={'ql-confirm-card'} role={'alertdialog'} aria-label={'Close the Quick Look?'}>
                    <h2>Your progress is saved.</h2>
                    <p>
                        The {Object.keys(answers).length} answer{Object.keys(answers).length === 1 ? '' : 's'} you
                        have given {Object.keys(answers).length === 1 ? 'is' : 'are'} kept in this browser. Come
                        back whenever and pick up on the question you left off at. Nothing has been sent to us.
                    </p>
                    <div className={'ql-confirm-actions'}>
                        <button className={'ql-btn'} onClick={() => setConfirming(false)} type={'button'}>
                            Keep going
                        </button>
                        <button className={'ql-btn-secondary'} onClick={onExit} type={'button'}>
                            Close and come back later
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    const getIntro = () => {
        return (
            <div className={'ql-intro'}>
                <h1>Let’s find the gap between where you are and where you <em>could</em> be.</h1>
                <p className={'ql-intro-lede'}>
                    A few questions about how your business runs today and where you’d like it
                    to go. At the end you get a straight read on the openings your answers point
                    to, and one thing you can do about it yourself, whether or not you ever
                    talk to us.
                </p>

                <div className={'ql-terms'}>
                    <div className={'ql-term'}>
                        <b>About three minutes</b>
                        <span>Seven questions, tap to answer. Nothing to type.</span>
                    </div>
                    <div className={'ql-term'}>
                        <b>No email required</b>
                        <span>Your results appear on screen. The email is optional, and comes after.</span>
                    </div>
                    <div className={'ql-term'}>
                        <b>Genuinely useful</b>
                        <span>You leave with something you can act on without hiring anyone.</span>
                    </div>
                </div>

                {isInProgress(saved.current) ? (
                    <div className={'ql-gate-actions ql-start'}>
                        <button className={'ql-btn'} onClick={onResume}>
                            Pick up where you left off <span className={'ql-arrow'}>→</span>
                        </button>
                        <button className={'ql-skip'} onClick={onRestart} type={'button'}>Start fresh</button>
                    </div>
                ) : (
                    <button className={'ql-btn ql-start'} onClick={() => { trackEvent('quick-look-started', { entry: 'intro' }); setStage(STAGES.core); }}>
                        Take a Quick Look <span className={'ql-arrow'}>→</span>
                    </button>
                )}
            </div>
        )
    }

    const getQuestion = () => {

        const total = questions.length;
        // only counts if the key still names one of the options in front of them
        const answered = answerFor(question, answers);

        return (
            <div className={'ql-question'}>
                <div className={'ql-progress'}>
                    <span className={'ql-progress-count'}>
                        {stage === STAGES.depth ? 'Deeper read · ' : ''}{index + 1} of {total}
                    </span>
                    <span className={'ql-progress-track'}>
                        <span className={'ql-progress-fill'} style={{ width: `${((index) / total) * 100}%` }} />
                    </span>
                </div>

                <h2>{question.question}</h2>
                {question.hint && <p className={'ql-question-hint'}>{question.hint}</p>}

                <div className={'ql-options'} role={'radiogroup'} aria-label={question.question}>
                    {question.options.map((option, position) => (
                        <button
                        aria-checked={answered === option.key}
                        className={'ql-option'}
                        key={option.key}
                        onClick={onAnswer.bind(this, question.id, option.key)}
                        role={'radio'}
                        type={'button'}>
                            {/* the number is a keyboard hint and a reading aid,
                                the only place a position still means anything */}
                            <span className={'ql-option-key'}>{position + 1}</span>
                            <span>{option.label}</span>
                        </button>
                    ))}
                </div>

                <button className={'ql-back'} onClick={onBack} type={'button'}>← Back</button>
            </div>
        )
    }

    // Opting in is itself the intent signal, so the choice is offered plainly
    // and skipping is a first-class option, not a grey afterthought.
    const getGate = () => {
        return (
            <div className={'ql-intro'}>
                <h1>You’re most of the way there.</h1>
                <p className={'ql-intro-lede'}>
                    A few more quick questions will sharpen your read. What you’d change first,
                    what you’ve already tried, and how much room there is to move. Or stop here
                    and read what you have.
                </p>
                <div className={'ql-gate-actions'}>
                    <button className={'ql-btn'} onClick={() => { trackEvent('quick-look-depth'); setIndex(0); setStage(STAGES.depth); }}>
                        Sharpen my read <span className={'ql-arrow'}>→</span>
                    </button>
                    <button className={'ql-skip'} onClick={() => { trackEvent('quick-look-completed', { depth: 'core', notes: 'no' }); setStage(STAGES.results); }} type={'button'}>
                        Show my results now
                    </button>
                </div>
            </div>
        )
    }

    /* The last screen before the read, and the only place in the whole tool
       anyone types. It comes after every tap-to-answer question and before the
       payoff, so it can be skipped in one click without costing anything. */
    const getNotes = () => {
        return (
            <div className={'ql-intro ql-notes'}>
                <h1>Anything else about where you’d like to take your business?</h1>
                <p className={'ql-intro-lede'}>
                    Optional, and there’s no wrong answer. A sentence about what made you
                    open this in the first place sharpens the read more than any question
                    we could have asked.
                </p>
                <textarea
                aria-label={'Anything else about where you’d like to take your business'}
                className={'ql-textarea ql-notes-box'}
                onChange={evt => setFreeText(evt.target.value)}
                placeholder={'The thing you’d fix or build first, if it were up to you.'}
                rows={4}
                value={freeText} />
                <div className={'ql-gate-actions'}>
                    <button className={'ql-btn'} onClick={onFinish}>
                        {freeText.trim() ? 'Show my read' : 'Skip and show my read'} <span className={'ql-arrow'}>→</span>
                    </button>
                    <button className={'ql-skip'} onClick={onBack} type={'button'}>Back</button>
                </div>
            </div>
        )
    }

    /* Shown while the read is being written. It is a few seconds of waiting, so
       it says what is happening rather than spinning — the honest answer, that
       this is being written for their answers, is also the most reassuring one
       about what kind of company they are dealing with. */
    const getReading = () => {
        return (
            <div className={'ql-reading'} role={'status'}>
                <ol className={'ql-steps'}>
                    {READING_STEPS.map((step, position) => (
                        <li
                        className={`ql-step ${position < readingStep ? 'is-done' : ''} ${position === readingStep ? 'is-live' : ''}`}
                        key={step}>
                            <span aria-hidden={'true'} className={'ql-step-mark'} />
                            <span>{step}</span>
                        </li>
                    ))}
                </ol>
            </div>
        )
    }

    /* The read. Structure and intent follow quick-look-results.md:

       reflection → the two or three areas → one free, usable action → the
       honest bridge into a conversation with a person.

       The order is the argument. Everything above the form is the value, and it
       stands on its own: an owner who closes the tab here has still been helped.
       The form is not a gate on any of it.

       Content comes from the generated read when there is one and from the
       authored read otherwise, and the two are interchangeable by construction —
       same three parts per area, same single action. Nothing on screen says
       which one produced it, because the owner has no use for that distinction.

       No score, no grade, no gauge, and no ranking language. */
    const getResults = () => {

        if(reading && !read) {
            return (
                <div className={'ql-read'}>
                    <header className={'ql-head'}>
                        <h1 className={'ql-verdict'}>Putting your read together.</h1>
                        {/* "A few seconds" was written when this was hoped to
                            be quick and never revised once it was measured. An
                            owner told a few seconds starts doubting the page at
                            about eight, and doubting it is the one thing this
                            screen exists to prevent. */}
                        <p className={'ql-lede'}>Usually under twenty seconds.</p>
                    </header>
                    {getReading()}
                </div>
            )
        }

        const result = scoreAnswers(answers);
        const areas = read ? read.areas : result.findings;
        const action = read ? read.action : result.action;
        const reflection = read ? read.reflection : result.reflection;

        return (
            <div className={'ql-read'}>
                <header className={'ql-head'}>
                    <h1 className={'ql-verdict'}>Here’s what we’re seeing.</h1>
                    <p className={'ql-lede'}>{reflection}</p>
                </header>

                {/* One document, one rhythm: heading, subhead, body — the same
                    system the articles are set in. The read has nothing a
                    reader of ours has not seen before, so it should not have to
                    be learned section by section. */}
                <div className={'ql-doc'}>

                    {areas.map((area, position) => (
                        <React.Fragment key={area.key || position}>
                            <h3>{area.title}</h3>
                            <p>{area.detail}</p>
                            <p>{area.meaning}</p>
                        </React.Fragment>
                    ))}

                    {/* The one thing on this screen the owner can act on without
                        us, lifted out of the prose. It is the only place the
                        accent appears in the read — emphasis has to be scarce to
                        mean anything. */}
                    <aside className={'ql-pull'}>
                        <span className={'ql-pull-label'}>Something you can do this week</span>
                        <p>{action}</p>
                    </aside>

                    {/* One piece of ours, picked against what they actually
                        answered, and only when something genuinely fits — most
                        reads end without it.

                        Beside the free action, not under the form. It sat at the
                        very foot of the screen on the argument that a link any
                        earlier competes with the form at the moment somebody is
                        deciding about it. That argument was about protecting the
                        form, and it cost the thing it was protecting: below a
                        long read and past an ask already answered or already
                        declined, a link nobody scrolls to is not a quieter offer,
                        it is no offer.

                        Here it is the second half of what the owner leaves with,
                        which is the order the home page promises — the useful
                        thing first, and then, separately, the conversation. */}
                    {read && read.article && (
                        <aside className={'ql-next'}>
                            <span className={'ql-next-label'}>While you’re here</span>
                            {/* The whole card is the link, not a "read more"
                                underneath it. Somebody who has decided they want
                                the piece should not then have to find the one
                                word that takes them to it. */}
                            <a
                            className={'ql-next-card'}
                            href={`/articles/${read.article.slug}`}
                            onClick={onArticle(read.article.slug)}>
                                <b>{read.article.title} <span className={'dl-arrow'}>→</span></b>
                                <span className={'ql-next-meta'}>
                                    {read.article.topic && <span>{read.article.topic}</span>}
                                    {read.article.minutes > 0 && <span>{read.article.minutes} min read</span>}
                                </span>
                                <span className={'ql-next-why'}>We wrote this one for a situation close to yours.</span>
                            </a>
                        </aside>
                    )}

                    {/* Last, and after both of the things we are giving away.
                        The ask is easier to make once it is plainly not the
                        price of anything above it. */}
                    {getLeadForm()}
                    {getCopyOffer()}
                </div>
            </div>
        )
    }

    /* The bridge and the form are one section, because they are one argument:
       this is the quick version, and the part actually worth having is a person
       going through it with you. Framed as being put in touch with someone,
       never as a document we will send — what the owner is agreeing to here is a
       conversation about their specifics.

       The SMS box is separate, unchecked, and never a condition of submitting. */
    const getLeadForm = () => {

        if(leadState === 'sent') {
            return (
                <>
                    {/* What happens next, when, and what it gets them. The line
                        that stood here promised "nothing to prepare, and nothing
                        is owed either way", which answers two worries nobody had
                        and raises the question of a bill at the one moment they
                        had stopped thinking about it. Confidence in a process
                        comes from being told exactly what it is. */}
                    <h2 className={'ql-ask'}>Thanks{lead.first.trim() ? `, ${lead.first.trim()}` : ''}. We’ve got it.</h2>
                    <p>
                        Your answers come through with this, and we read them before we get in
                        touch. That means the conversation starts where this one left off rather
                        than at the beginning, and you won’t be asked any of it twice. You’ll hear
                        from us within a day or two.
                        {lead.sms ? ' We’ll only text you because you said we could, and STOP stops it any time.' : ''}
                    </p>
                    <a className={'ql-email-sent-link'} href={'/contact'} onClick={onContact}>
                        Or reach us directly
                    </a>
                </>
            )
        }

        const set = field => evt => setLead({ ...lead, [field]: evt.target.value });
        const required = <span aria-hidden={'true'} className={'ql-req'} />;

        return (
            <>
                <h3 className={'ql-ask'}>This is the quick version. The next part is a conversation.</h3>
                <p>
                    What you have read is the pattern we can see from a few answers. What is
                    actually worth doing about it, which piece to take first, what it would
                    take, and how it fits with the tools you already run, depends on things
                    a set of questions can’t reach. So the next step isn’t a longer document.
                    It’s one of us going through this with you: free, no obligation, and no
                    pitch waiting at the end of it.
                </p>
                {/* Named here rather than left for How We Work to mention.

                    That page describes a paid Fit Assessment as what comes after
                    this, and for a while this block said the next step was free
                    — so an owner who read both saw a firm that had not decided
                    what it sells. It is one sentence to say that both are true
                    and which order they come in, and saying it here is the only
                    place it lands before the decision rather than after it.

                    Deliberately without a number. A price quoted before anyone
                    has heard the problem is the thing the rest of the site
                    argues against, and the honest version of this is that most
                    people will not need it. */}
                <p className={'ql-note'}>
                    Some problems turn out to need a proper diagnosis before anyone can
                    say what to build. That is a paid piece of work and we will tell you
                    plainly if yours is one of them. But it comes after the conversation,
                    not instead of it, and plenty of them never get that far.
                </p>

                <form className={'ql-form'} onSubmit={onSubmitLead}>
                    <BotTrap />
                    <div className={'ql-form-row'}>
                        <div className={'ql-field'}>
                            <label htmlFor={'ql-first'}>First name {required}</label>
                            <input
                            autoComplete={'given-name'}
                            className={'ql-input'}
                            id={'ql-first'}
                            onChange={set('first')}
                            required={true}
                            type={'text'}
                            value={lead.first} />
                        </div>
                        <div className={'ql-field'}>
                            <label htmlFor={'ql-last'}>Last name {required}</label>
                            <input
                            autoComplete={'family-name'}
                            className={'ql-input'}
                            id={'ql-last'}
                            onChange={set('last')}
                            required={true}
                            type={'text'}
                            value={lead.last} />
                        </div>
                        <div className={'ql-field'}>
                            <label htmlFor={'ql-email'}>Email {required}</label>
                            <input
                            autoComplete={'email'}
                            className={'ql-input'}
                            id={'ql-email'}
                            onChange={set('email')}
                            required={true}
                            type={'email'}
                            value={lead.email} />
                        </div>
                        {/* Optional, and only required if they tick the box below —
                            a consent record with no number on it is worth nothing to
                            either side. */}
                        <div className={'ql-field'}>
                            <label htmlFor={'ql-phone'}>
                                Phone {lead.sms ? required : null}
                            </label>
                            <input
                            autoComplete={'tel'}
                            className={'ql-input'}
                            id={'ql-phone'}
                            onChange={set('phone')}
                            required={lead.sms}
                            type={'tel'}
                            value={lead.phone} />
                        </div>
                    </div>

                    {/* Unchecked, always. Consent is an affirmative act with a date on
                        it — bundling it into the submit button would make it worthless
                        as a record and a real exposure. See onSubmitLead. */}
                    <label className={'ql-check'} htmlFor={'ql-sms'}>
                        <input
                        checked={lead.sms}
                        id={'ql-sms'}
                        onChange={evt => setLead({ ...lead, sms: evt.target.checked })}
                        type={'checkbox'} />
                        <span>{SMS_CONSENT_TEXT}</span>
                    </label>

                    <div className={'ql-form-send'}>
                        <button className={'ql-btn'} disabled={leadState === 'sending'} type={'submit'}>
                            {leadState === 'sending' ? 'Sending…' : 'Send'} <span className={'ql-arrow'}>→</span>
                        </button>
                    </div>

                    {typeof leadState === 'string' && leadState !== 'sending' && (
                        <p className={'ql-email-error'} role={'alert'}>{leadState}</p>
                    )}
                </form>
            </>
        )
    }

    /* Asking for a copy of the read.

       Only offered when there is a generated read to send: the authored fallback
       is the same words for everybody and emailing it would be a form letter
       dressed as a personal one. read.id is the uuid the server wrote, so what
       gets sent is fetched there rather than posted from here. */
    const onSendCopy = async evt => {

        evt.preventDefault();
        if(copyState === 'sending') return;
        const trap = trapValue(evt);

        setCopyState('sending');
        try {
            const response = await fetch(`/api/v${API.version}/quick_look/`, {
                body: JSON.stringify({
                    company_website: trap,
                    email: copyTo.trim(),
                    first_name: lead.first.trim() || null,
                    read_id: read ? read.id : null,
                    type: 'deliver'
                }),
                headers: { 'Content-Type': 'application/json' },
                method: 'POST'
            });
            if(!response.ok) throw new Error('that did not go through');
            setCopyState('sent');
            trackEvent('quick-look-copy-sent', { from: 'results' });

        } catch(e) {
            setCopyState('That did not send. Email hello@duralabs.io and we will send it over.');
        }
    };

    /* Offered after the ask to talk, not before it.

       Above the form it is an easy way out of a decision somebody has not made
       yet, and the two would compete. Below it, it catches the larger group —
       the ones who found the read useful, are not ready for a conversation, and
       would otherwise close the tab with nothing.

       Hidden once the lead is in, because that path already sends this. */
    const getCopyOffer = () => {

        if(!read || !read.id || leadState === 'sent') {
            return null;
        }

        if(copyState === 'sent') {
            return (
                <p className={'ql-copy-done'}>
                    Sent. It is a link you can keep, and forward to anybody else who has a say in this.
                </p>
            )
        }

        return (
            <form className={'ql-copy'} onSubmit={onSendCopy}>
                <BotTrap />
                <p className={'ql-copy-ask'}>Not ready for that? Have the read sent to you instead.</p>
                <div className={'ql-copy-row'}>
                    <input
                    aria-label={'Email address'}
                    autoComplete={'email'}
                    className={'ql-input'}
                    onChange={evt => setCopyTo(evt.target.value)}
                    placeholder={'you@company.com'}
                    required={true}
                    type={'email'}
                    value={copyTo} />
                    <button className={'ql-btn-secondary'} disabled={copyState === 'sending'} type={'submit'}>
                        {copyState === 'sending' ? 'Sending…' : 'Send it'}
                    </button>
                </div>
                <p className={'ql-copy-note'}>Just the read. Nothing else, and no mailing list.</p>
                {typeof copyState === 'string' && copyState !== 'sending' && (
                    <p className={'ql-email-error'} role={'alert'}>{copyState}</p>
                )}
            </form>
        )
    };

    const getStage = () => {
        switch(stage) {
            case STAGES.intro: return getIntro();
            case STAGES.gate: return getGate();
            case STAGES.notes: return getNotes();
            case STAGES.results: return getResults();
            default: return getQuestion();
        }
    }

    /* A click that starts on the dim ground closes; one that starts inside the
       panel and ends outside it (a drag while selecting text) must not. Hence
       mousedown on the target itself rather than a click handler. */
    const onBackdrop = evt => {
        if(evt.target === evt.currentTarget) {
            onRequestClose();
        }
    }

    return (
        <div className={`ql-overlay ${leaving ? 'is-leaving' : ''}`} onMouseDown={onBackdrop}>
            <div
            aria-label={'Quick Look'}
            aria-modal={'true'}
            className={'ql'}
            role={'dialog'}>
                {getBar()}
                <div className={'ql-stage'} ref={stageRef} tabIndex={-1}>
                    <div className={'ql-anim'} key={`${stage}-${index}`}>
                        {getStage()}
                    </div>
                </div>
                {/* The read is long enough that anything parked under it is
                    effectively hidden. These two are not part of the read, so
                    they sit outside the scroll and stay where they are. */}
                {stage === STAGES.results && (
                    <div className={'ql-footer'}>
                        <div className={'ql-footer-actions'}>
                            <button className={'ql-btn-quiet'} onClick={onReview} type={'button'}>Change my answers</button>
                            <button className={'ql-btn-quiet'} onClick={onRestart} type={'button'}>Start over</button>
                        </div>
                    </div>
                )}
                {confirming && getConfirm()}
            </div>
        </div>
    )
}

export default QuickLook;
